import { Row, RowTransformFunction, RowValidate } from '@fast-csv/parse/build/src/types';
import { notification, UploadFile } from '@pankod/refine-antd';
import { parseString } from 'fast-csv';

const SUPPORTED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif'];
const SUPPORTED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heic', '.heif'];
// NOTE : we are not using this variable in production as its using by default value 50 MB 
export const MAX_UPLOAD_FILE_SIZE_MB = Number(process.env.REACT_APP_MAX_UPLOAD_FILE_SIZE_MB || 50); 

export const isJpgOrPngFile = (type: string, fileName = '') => {
  const normalizedType = type.toLowerCase();
  if (SUPPORTED_IMAGE_MIME_TYPES.includes(normalizedType)) {
    return true;
  }

  const normalizedFileName = fileName.toLowerCase();
  return SUPPORTED_IMAGE_EXTENSIONS.some((extension) => normalizedFileName.endsWith(extension));
};

export const isPurchaseReceiptFile = (type: string, fileName = '') => {
  return isJpgOrPngFile(type, fileName) || type.toLowerCase() === 'application/pdf';
};

export const isLessThanSize = (size: number, compareValue: number) => {
  return size / 1024 / 1024 < compareValue;
};

export const beforeImageUploadValidation = (file: UploadFile) => {
  const format = file.type || '';
  if (!isJpgOrPngFile(format, file.name || '')) {
    // message.error('You can only upload JPG/PNG file!', 5);
    notification.error({
      message: 'Failed',
      description: 'You can only upload JPG/PNG/HEIC/HEIF file!',
    });
    return true;
  }

  const size = file.size || 0;
  if (!isLessThanSize(size, MAX_UPLOAD_FILE_SIZE_MB)) {
    notification.error({
      message: 'Failed',
      description: `Image must smaller than ${MAX_UPLOAD_FILE_SIZE_MB}MB!`,
    });
    return true;
  }

  return false;
};

export const beforeReceiptUploadValidation = (file: UploadFile) => {
  const format = file.type || '';
  if (!isPurchaseReceiptFile(format, file.name || '')) {
    notification.error({
      message: 'Failed',
      description: 'You can only upload JPG/PNG/HEIC/HEIF or PDF file!',
    });
    return true;
  }

  const size = file.size || 0;
  if (!isLessThanSize(size, MAX_UPLOAD_FILE_SIZE_MB)) {
    notification.error({
      message: 'Failed',
      description: `File must smaller than ${MAX_UPLOAD_FILE_SIZE_MB}MB!`,
    });
    return true;
  }

  return false;
};

export const getStringFromFile = async (file: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

interface ReadCSV {
  file: Blob;
  transformFunction?: RowTransformFunction<Row<any>, Row<any>>;
  validateFunction?: RowValidate<Row<any>>;
}

export const readCSV = async ({
  file,
  transformFunction,
  validateFunction,
}: ReadCSV): Promise<any[]> => {
  const csvString = await getStringFromFile(file);

  const jsonData: any[] = [];
  return new Promise((resolve, reject) => {
    const result = parseString(csvString, {
      headers: (headers) => headers.map((h) => h?.trim()),
      ignoreEmpty: true,
    })
      .on('error', (error) => reject(error))
      .on('data-invalid', (row, rowNumber) =>
        reject(new Error(`Invalid [rowNumber=${rowNumber}] [row=${JSON.stringify(row)}]`))
      )
      .on('data', (row) => jsonData.push(row))
      .on('end', (_rowCount: number) => {
        resolve(jsonData);
      });

    if (transformFunction) {
      result.transform(transformFunction);
    }

    if (validateFunction) {
      result.validate(validateFunction);
    }
  });
};

export const downloadCSVFile = (data: string[][]) => {
  let csvContent = 'data:text/csv;charset=utf-8,';
  csvContent += data.map((e) => e.join(',')).join('\n');
  const encodedUri = encodeURI(csvContent);
  const fixedEncodedURI = encodedUri.replaceAll('#', '%23');
  const link = document.createElement('a');
  link.setAttribute('id', 'downloadLink');
  link.setAttribute('href', fixedEncodedURI);
  link.setAttribute('download', new Date().getTime() + '.csv');
  document.body.appendChild(link);
  link.click();
  document.getElementById('downloadLink')?.remove();
};
